import { React, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FairLaunchTheme } from "../CeateFairLaunch/FairLaunchTheme";

const AdditionalInfo = ({
  handleNext,
  activeStep,
  steps,
  handleBack,
  stepData,
  setStepData,
}) => {
  const neonInputStyle = FairLaunchTheme.inputStyle;

  const [logoPreview, setLogoPreview] = useState("");
  const [logoError, setLogoError] = useState(false);

  const formik = useFormik({
    initialValues: {
      title: stepData.title || "",
      logoUrl: stepData.logoUrl || "",
      websiteUrl: stepData.websiteUrl || "",
      facebookUrl: stepData.facebookUrl || "",
      twitterUrl: stepData.twitterUrl || "",
      githubUrl: stepData.githubUrl || "",
      telegramUrl: stepData.telegramUrl || "",
      instagramUrl: stepData.instagramUrl || "",
      discordUrl: stepData.discordUrl || "",
      redditUrl: stepData.redditUrl || "",
      youtubeUrl: stepData.youtubeUrl || "",
      description: stepData.description || "",
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .trim()
        .required("Title is required")
        .max(100, "Title must be at most 100 characters"),
      logoUrl: Yup.string()
        .trim()
        .matches(
          /\.(jpeg|jpg|gif|png|svg)$/i,
          "Must be a valid image URL (jpg, jpeg, png, gif, svg)"
        )
        .required("Logo URL is required"),
      websiteUrl: Yup.string().url("Invalid URL"),
      facebookUrl: Yup.string().url("Invalid URL"),
      twitterUrl: Yup.string().url("Invalid URL"),
      githubUrl: Yup.string().url("Invalid URL"),
      telegramUrl: Yup.string().url("Invalid URL"),
      instagramUrl: Yup.string().url("Invalid URL"),
      discordUrl: Yup.string().url("Invalid URL"),
      redditUrl: Yup.string().url("Invalid URL"),
      youtubeUrl: Yup.string().url("Invalid URL"),
      description: Yup.string()
        .required("Description is required")
        .min(50, "Description should be at least 50 characters")
        .max(500, "Description should not exceed 500 characters"),
    }),
    onSubmit: (values) => {
      const newStepData = {
        additionalInfo: {
          title: values.title,
          logoUrl: values.logoUrl,
          websiteUrl: values.websiteUrl,
          facebookUrl: values.facebookUrl,
          twitterUrl: values.twitterUrl,
          githubUrl: values.githubUrl,
          telegramUrl: values.telegramUrl,
          instagramUrl: values.instagramUrl,
          discordUrl: values.discordUrl,
          redditUrl: values.redditUrl,
          youtubeUrl: values.youtubeUrl,
          description: values.description,
        },
      };
      setStepData((prev) => ({ ...prev, ...newStepData }));
      handleNext(newStepData);
    },
  });

  useEffect(() => {
    const url = formik.values.logoUrl || "";
    if (url && /\.(jpg|jpeg|png|gif|svg)$/i.test(url)) {
      setLogoPreview(url);
      setLogoError(false);
    } else {
      setLogoPreview("");
    }
  }, [formik.values.logoUrl]);

  return (
    <Box sx={(theme) => FairLaunchTheme.cardStyle(theme)}>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={4}>
          {/* Title Field */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={(theme) => ({
                ...FairLaunchTheme.gradientText(theme),
                mb: 1.5,
              })}
            >
              Project Title *
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter project title"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
              sx={neonInputStyle}
            />
          </Grid>

          {/* Logo URL Field */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={(theme) => ({
                ...FairLaunchTheme.gradientText(theme),
                mb: 1.5,
              })}
            >
              Logo URL *
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter Logo URL"
              name="logoUrl"
              value={formik.values.logoUrl}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.logoUrl && Boolean(formik.errors.logoUrl)) ||
                logoError
              }
              helperText={
                logoError ? (
                  <span style={{ color: "#FF4444" }}>
                    Image could not be loaded. Please check the URL.
                  </span>
                ) : (
                  formik.touched.logoUrl && formik.errors.logoUrl
                )
              }
              sx={neonInputStyle}
            />

            {/* Logo Preview */}
            {logoPreview && !logoError && (
              <Box
                mt={2}
                sx={{
                  p: 1,
                  border: "1px solid #333",
                  borderRadius: "8px",
                  display: "inline-block",
                }}
              >
                <Typography variant="subtitle2" sx={{ color: "#DDD", mb: 1 }}>
                  Logo Preview:
                </Typography>
                <Box
                  component="img"
                  src={logoPreview}
                  alt="Logo Preview"
                  sx={{
                    maxWidth: "120px",
                    maxHeight: "120px",
                    borderRadius: "8px",
                  }}
                  onError={() => {
                    setLogoError(true);
                    setLogoPreview(""); // hide broken image
                  }}
                  onLoad={() => setLogoError(false)}
                />
              </Box>
            )}

            <Typography
              variant="body2"
              sx={{ mt: 1, color: "#9CA3AF", display: "block" }}
            >
              URL must end with a supported image extension png, jpg, jpeg or
              gif. You can upload your image at{" "}
              <a
                href="http://upload.trendsale.finance/"
                style={{ color: "#00FFFF" }}
                target="_blank"
                rel="noreferrer"
              >
                upload.trendsale.finance
              </a>
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="h6"
              sx={(theme) => ({
                ...FairLaunchTheme.gradientText(theme),
                mb: 1.5,
              })}
            >
              Website*
            </Typography>
            <TextField
              name="websiteUrl"
              variant="outlined"
              placeholder="Ex: https://..."
              value={formik.values.websiteUrl}
              onChange={formik.handleChange}
              error={
                formik.touched.websiteUrl && Boolean(formik.errors.websiteUrl)
              }
              helperText={formik.touched.websiteUrl && formik.errors.websiteUrl}
              fullWidth
              sx={neonInputStyle}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="h6"
              sx={(theme) => ({
                ...FairLaunchTheme.gradientText(theme),
                mb: 1.5,
              })}
            >
              Facebook
            </Typography>
            <TextField
              name="facebookUrl"
              variant="outlined"
              placeholder="Ex: https://facebook.com/..."
              value={formik.values.facebookUrl}
              onChange={formik.handleChange}
              error={
                formik.touched.facebookUrl && Boolean(formik.errors.facebookUrl)
              }
              helperText={
                formik.touched.facebookUrl && formik.errors.facebookUrl
              }
              fullWidth
              sx={neonInputStyle}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="h6"
              sx={(theme) => ({
                ...FairLaunchTheme.gradientText(theme),
                mb: 1.5,
              })}
            >
              Twitter
            </Typography>
            <TextField
              name="twitterUrl"
              variant="outlined"
              placeholder="Ex: https://twitter.com/..."
              value={formik.values.twitterUrl}
              onChange={formik.handleChange}
              error={
                formik.touched.twitterUrl && Boolean(formik.errors.twitterUrl)
              }
              helperText={formik.touched.twitterUrl && formik.errors.twitterUrl}
              fullWidth
              sx={neonInputStyle}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="h6"
              sx={(theme) => ({
                ...FairLaunchTheme.gradientText(theme),
                mb: 1.5,
              })}
            >
              Github
            </Typography>
            <TextField
              name="githubUrl"
              variant="outlined"
              placeholder="Ex: https://github.com/..."
              value={formik.values.githubUrl}
              onChange={formik.handleChange}
              error={
                formik.touched.githubUrl && Boolean(formik.errors.githubUrl)
              }
              helperText={formik.touched.githubUrl && formik.errors.githubUrl}
              fullWidth
              sx={neonInputStyle}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="h6"
              sx={(theme) => ({
                ...FairLaunchTheme.gradientText(theme),
                mb: 1.5,
              })}
            >
              Telegram
            </Typography>
            <TextField
              name="telegramUrl"
              variant="outlined"
              placeholder="Ex: https://t.me/..."
              value={formik.values.telegramUrl}
              onChange={formik.handleChange}
              error={
                formik.touched.telegramUrl && Boolean(formik.errors.telegramUrl)
              }
              helperText={
                formik.touched.telegramUrl && formik.errors.telegramUrl
              }
              fullWidth
              sx={neonInputStyle}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="h6"
              sx={(theme) => ({
                ...FairLaunchTheme.gradientText(theme),
                mb: 1.5,
              })}
            >
              Instagram
            </Typography>
            <TextField
              name="instagramUrl"
              variant="outlined"
              placeholder="Ex: https://instagram.com/..."
              value={formik.values.instagramUrl}
              onChange={formik.handleChange}
              error={
                formik.touched.instagramUrl &&
                Boolean(formik.errors.instagramUrl)
              }
              helperText={
                formik.touched.instagramUrl && formik.errors.instagramUrl
              }
              fullWidth
              sx={neonInputStyle}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="h6"
              sx={(theme) => ({
                ...FairLaunchTheme.gradientText(theme),
                mb: 1.5,
              })}
            >
              Discord
            </Typography>
            <TextField
              name="discordUrl"
              variant="outlined"
              placeholder="Ex: https://discord.gg/..."
              value={formik.values.discordUrl}
              onChange={formik.handleChange}
              error={
                formik.touched.discordUrl && Boolean(formik.errors.discordUrl)
              }
              helperText={formik.touched.discordUrl && formik.errors.discordUrl}
              fullWidth
              sx={neonInputStyle}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              variant="h6"
              sx={(theme) => ({
                ...FairLaunchTheme.gradientText(theme),
                mb: 1.5,
              })}
            >
              Reddit
            </Typography>
            <TextField
              name="redditUrl"
              variant="outlined"
              placeholder="Ex: https://reddit.com/r/..."
              value={formik.values.redditUrl}
              onChange={formik.handleChange}
              error={
                formik.touched.redditUrl && Boolean(formik.errors.redditUrl)
              }
              helperText={formik.touched.redditUrl && formik.errors.redditUrl}
              fullWidth
              sx={neonInputStyle}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="h6"
              sx={(theme) => ({
                ...FairLaunchTheme.gradientText(theme),
                mb: 1.5,
              })}
            >
              Youtube Video
            </Typography>
            <TextField
              name="youtubeUrl"
              variant="outlined"
              placeholder="Ex: https://youtube.com/watch?v=..."
              value={formik.values.youtubeUrl}
              onChange={formik.handleChange}
              error={
                formik.touched.youtubeUrl && Boolean(formik.errors.youtubeUrl)
              }
              helperText={formik.touched.youtubeUrl && formik.errors.youtubeUrl}
              fullWidth
              sx={neonInputStyle}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={(theme) => ({
                ...FairLaunchTheme.gradientText(theme),
                mb: 1.5,
              })}
            >
              Description*
            </Typography>
            <TextField
              name="description"
              variant="outlined"
              placeholder="Ex: This is the best project..."
              multiline
              rows={4}
              value={formik.values.description}
              onChange={formik.handleChange}
              error={
                formik.touched.description && Boolean(formik.errors.description)
              }
              helperText={
                formik.touched.description && formik.errors.description
              }
              fullWidth
              sx={neonInputStyle}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}
          >
            <Button
              onClick={handleBack}
              sx={(theme) => ({
                ...FairLaunchTheme.neonButton(theme),
                borderColor: theme.palette.divider,
                color: theme.palette.text.secondary,
                "&:hover": {
                  borderColor: theme.palette.text.primary,
                  color: theme.palette.text.primary,
                  background: "rgba(255, 255, 255, 0.05)",
                },
              })}
            >
              Back
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={(theme) => ({
                ...FairLaunchTheme.neonButton(theme),
                minWidth: "200px",
              })}
            >
              {activeStep === steps.length - 1 ? "Finish" : "Next"}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
export default AdditionalInfo;
